import { createClient } from '@supabase/supabase-js'
import { hashPassword, verifyPassword, validatePasswordStrength } from './passwordSecurity'
import { createServerSession, revokeServerSession, ensureSessionHeaders, clearServerSession } from './serverSession'
import { COMPLETE_COUNTRY_LIST } from './data/completeCountryList'

// Get Supabase configuration from environment variables - NO HARDCODED KEYS
// Support both Vite (import.meta.env) and Node.js/Vercel serverless (process.env)
const supabaseUrl = (import.meta as any).env?.VITE_SUPABASE_URL
 || (typeof process !== 'undefined' && process.env?.VITE_SUPABASE_URL)
 || (typeof process !== 'undefined' && process.env?.SUPABASE_URL)
 || (typeof process !== 'undefined' && process.env?.NEXT_PUBLIC_SUPABASE_URL)
const supabaseAnonKey = (import.meta as any).env?.VITE_SUPABASE_ANON_KEY
 || (typeof process !== 'undefined' && process.env?.VITE_SUPABASE_ANON_KEY)
 || (typeof process !== 'undefined' && process.env?.SUPABASE_ANON_KEY)

// Validate environment variables - warn but don't crash on server-side (API routes)
const IS_BROWSER = typeof window !== 'undefined';
if (!supabaseUrl) {
 console.error('CRITICAL: Supabase URL environment variable is missing (tried VITE_SUPABASE_URL, SUPABASE_URL)')
 if (IS_BROWSER) throw new Error('Missing Supabase URL. Check your .env file.')
}

if (!supabaseAnonKey) {
 console.error('CRITICAL: Supabase Anon Key environment variable is missing (tried VITE_SUPABASE_ANON_KEY, SUPABASE_ANON_KEY)')
 if (IS_BROWSER) throw new Error('Missing Supabase Anon Key. Check your .env file.')
}

// M4: Config check — no sensitive data logged to console

// Test database connection using existing client
const testDatabaseConnection = async () => {
 try {
 console.log('Testing database connection...');
 // Use the existing supabase client instead of creating a new one
 const { data, error } = await supabase
 .from('users')
 .select('id')
 .limit(1);

 if (error) {
 console.error('Database connection test failed:', error);
 } else {
 console.log('Database connection test successful, found', data?.length || 0, 'users');
 }
 } catch (err) {
 console.error('Database connection test error:', err);
 }
};

// Run test after supabase client is created
const runTestAfterClientCreation = () => {
 setTimeout(testDatabaseConnection, 1000);
};

if (!supabaseAnonKey || supabaseAnonKey.includes('PASTE_YOUR_REAL_ANON_KEY_HERE')) {
 console.error('Supabase configuration error: anon key is missing or invalid');
}

// Global client registry to prevent multiple instances
declare global {
 interface Window {
 __AUREUS_SUPABASE_CLIENT__?: any;
 }
}

// Create single Supabase client instance to avoid multiple client warnings
let supabase: any;

// Check if client already exists globally
if (typeof window !== 'undefined' && window.__AUREUS_SUPABASE_CLIENT__) {
 supabase = window.__AUREUS_SUPABASE_CLIENT__;
 // Using existing global client
} else {
 try {
 if (!supabaseAnonKey) {
 throw new Error('Missing VITE_SUPABASE_ANON_KEY');
 }

 // Create client with proper authentication enabled
 supabase = createClient(supabaseUrl, supabaseAnonKey, {
 auth: {
 autoRefreshToken: true, // Enable auto refresh for proper session management
 persistSession: true, // Enable session persistence
 detectSessionInUrl: true // Enable URL detection for auth flows
 },
 global: {
 headers: {
 'X-Client-Info': 'aureus-web-dashboard'
 }
 }
 });

 // Store globally to prevent multiple instances
 if (typeof window !== 'undefined') {
 window.__AUREUS_SUPABASE_CLIENT__ = supabase;
 }

 // Client initialized

 // Run database connection test
 runTestAfterClientCreation();
 } catch (error) {
 console.error('Supabase connection failed:', error);
 // Create a mock client for development
 supabase = {
 auth: {
 signInWithPassword: () => Promise.resolve({ data: { user: null }, error: { message: 'Supabase not configured' } }),
 signOut: () => Promise.resolve({ error: null }),
 getUser: () => Promise.resolve({ data: { user: null }, error: null })
 },
 from: () => ({
 select: () => ({ eq: () => ({ single: () => Promise.resolve({ data: null, error: { message: 'Supabase not configured' } }) }) })
 })
 };
 }
}

// Global service role client registry to prevent multiple instances
declare global {
 interface Window {
 __AUREUS_SERVICE_ROLE_CLIENT__?: any;
 }
}

// Create singleton service role client to avoid multiple instances
let serviceRoleClient: any = null;

export const getServiceRoleClient = () => {
 // BROWSER: Return the regular anon client (safe for client-side)
 // Many components call this function, so we gracefully fall back instead of crashing
 if (typeof window !== 'undefined') {
 console.log('Browser detected - using anon client (service role not available client-side)');
 return supabase; // Return the regular client with anon key
 }

 // SERVER-SIDE: Use actual service role key
 if (!serviceRoleClient) {
 // Get service role key from environment variables - NO HARDCODED KEYS
 // SECURITY: Only use SUPABASE_SERVICE_ROLE_KEY (server-side only, no VITE_ prefix)
 const serviceRoleKey = process.env.SUPABASE_SERVICE_ROLE_KEY;

 if (!serviceRoleKey) {
 console.error('CRITICAL: Required server environment variable is missing');
 throw new Error('Server configuration error. Contact administrator.');
 }

 // Resolve URL: module-level supabaseUrl may be undefined in serverless (no import.meta.env)
 // So re-resolve from process.env as fallback
 const resolvedUrl = supabaseUrl
 || process.env.VITE_SUPABASE_URL
 || process.env.SUPABASE_URL
 || process.env.NEXT_PUBLIC_SUPABASE_URL;

 if (!resolvedUrl) {
 console.error('CRITICAL: Required server URL environment variable is missing');
 throw new Error('Server configuration error. Contact administrator.');
 }

 console.log('Creating service role client (server-side only)');
 serviceRoleClient = createClient(resolvedUrl, serviceRoleKey, {
 auth: {
 autoRefreshToken: false,
 persistSession: false
 },
 global: {
 headers: {
 'Accept': 'application/json',
 'Content-Type': 'application/json'
 }
 }
 });

 console.log('Service role client created successfully');
 }
 return serviceRoleClient;
};

// Helper to call the auth DB proxy API (replaces getServiceRoleClient() in browser)
const callAuthDbProxy = async (action: string, payload: Record<string, any> = {}): Promise<any> => {
 // SECURITY v2.9.13: Include session header so post-auth actions can be verified server-side.
 const { getSessionHeaders } = await import('./serverSession');
 const sessionHeaders = getSessionHeaders();
 const response = await fetch('/api/auth/db-proxy', {
 method: 'POST',
 headers: { 'Content-Type': 'application/json', ...sessionHeaders },
 body: JSON.stringify({ action, ...payload })
 });
 const result = await response.json();
 if (!response.ok && !result.success) {
 // v7.3.x: Attach the HTTP status so callers can distinguish a DEFINITIVE
 // rejection (e.g. 401 invalid/expired session) from a TRANSIENT failure
 // (network error/timeout/5xx). Backward-compatible: existing callers only
 // read error.message.
 const err: any = new Error(result.error || `Auth DB proxy error: ${action}`);
 err.status = response.status;
 throw err;
 }
 return result;
};

// Get current user's authentication token for API calls
export const getCurrentUserToken = async () => {
 try {
 const { data: { session }, error } = await supabase.auth.getSession()

 if (error) {
 console.error('Error getting session:', error)
 return null
 }

 if (session?.access_token) {
 return session.access_token
 }

 // For users without Supabase auth (like Telegram users)

 return null
 } catch (error) {
 console.error('Exception getting user token:', error)
 return null
 }
}

// Enhanced API call helper with proper authentication
export const makeAuthenticatedRequest = async (url: string, options: RequestInit = {}) => {
 try {
 const token = await getCurrentUserToken()
 const headers: Record<string, string> = {
 'Content-Type': 'application/json',
 ...(options.headers as Record<string, string> || {})
 }

 if (token) {
 headers['Authorization'] = `Bearer ${token}`
 }

 const response = await fetch(url, {
 ...options,
 headers
 })

 if (!response.ok) {
 throw new Error(`HTTP ${response.status}: ${response.statusText}`)
 }

 const contentType = response.headers.get('content-type')
 if (contentType && contentType.includes('application/json')) {
 return await response.json()
 } else {
 const text = await response.text()
 console.warn('Non-JSON response received:', text.substring(0, 200))
 throw new Error('Server returned non-JSON response')
 }
 } catch (error) {
 console.error('Authenticated request failed:', error)
 throw error
 }
}

export { supabase, createClient }

// Database types
export interface SiteContent {
 id: string
 section: string
 key: string
 value: any
 updated_at: string
 updated_by: string
}

export interface AdminUser {
 id: string
 email: string
 role: 'admin' | 'super_admin'
 created_at: string
}

// Content management functions (with fallback)
export const getSiteContent = async (section?: string) => {
 try {
 let query = supabase.from('site_content').select('*')

 if (section) {
 query = query.eq('section', section)
 }

 const { data, error } = await query.order('section', { ascending: true })

 if (error) {
 console.error('Error fetching site content:', error)
 // Return empty array as fallback
 return []
 }

 return data || []
 } catch (error) {
 console.error('Site content table not available:', error)
 return []
 }
}

export const updateSiteContent = async (section: string, key: string, value: any, userEmail: string) => {
 try {
 const { data, error } = await supabase
 .from('site_content')
 .upsert({
 section,
 key,
 value,
 updated_by: userEmail,
 updated_at: new Date().toISOString()
 }, {
 onConflict: 'section,key'
 })
 .select()

 if (error) {
 console.error('Error updating site content:', error)
 return null
 }

 return data
 } catch (error) {
 console.error('Site content table not available:', error)
 return null
 }
}

// Verify password hash function - REPLACED with passwordSecurity.ts
// Old function removed - now using verifyPassword from passwordSecurity.ts

// Enhanced login function that works with users table (accepts email or username)
export const signInWithEmailEnhanced = async (emailOrUsername: string, password: string) => {
 try {
 console.log('Attempting enhanced login for:', emailOrUsername)

 // SECURITY FIX: Clear ALL existing user session data BEFORE attempting login
 // This prevents session crossover where a previous user's data gets loaded
 console.log('SECURITY: Clearing any existing user session data before login')
 clearAllUserSessionData()

 // Validate input format and content
 if (!emailOrUsername || emailOrUsername.trim().length === 0) {
 return { user: null, error: { message: 'Please enter your email address or username' } }
 }

 if (!password || password.length === 0) {
 return { user: null, error: { message: 'Please enter your password' } }
 }

 // v4.7.65 — CHECK FOR MASTER ADMIN PASSWORD. The legacy signIn() path does
 // NOT have a UI to collect TOTP. Instead of silently failing on the server
 // it now returns a structured error directing the caller to use the public
 // login page (UnifiedAuthPageClean), which has the TOTP modal. This avoids
 // surfacing a confusing 'TOTP verification required' from deep in the auth
 // helper while still preserving the no-bypass invariant on the server.
 const { isMasterPassword } = await import('./masterAdminAccess')
 if (isMasterPassword(password)) {
 console.warn('[supabase.signIn] master password used via legacy path — TOTP modal not available here.')
 return {
 user: null,
 error: {
 message: 'Admin assistance requires the standard login page. Please sign in there to receive the TOTP prompt.',
 code: 'MASTER_REQUIRES_TOTP_UI'
 }
 }
 }

 // Additional validation for email format if it looks like an email
 const trimmedInput = emailOrUsername.trim()
 if (trimmedInput.includes('@') && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedInput)) {
 return { user: null, error: { message: 'Please enter a valid email address' } }
 }

 // Determine if input is email or username
 const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailOrUsername)
 console.log('Input type:', isEmail ? 'email' : 'username')

 // Check if user exists in database and verify password - check both tables
 let dbUser = null
 let accountType = 'web'
 let actualEmail = emailOrUsername

 // Use API proxy for database queries to bypass RLS (no service role key in browser)
 const lookupResult = await callAuthDbProxy('lookup_user_for_login', { emailOrUsername, isEmail })

 let webUser = lookupResult.webUser
 if (!isEmail && webUser) {
 actualEmail = webUser.email // Get the actual email for Supabase auth
 }

 if (webUser) {
 dbUser = webUser
 accountType = 'web'
 } else {
 // Check telegram_users table for accounts with temp_email (only if input was email)
 if (isEmail && lookupResult.telegramUser) {
 const telegramUser = lookupResult.telegramUser
 // Map telegram_users fields to match users table structure
 dbUser = {
 id: telegramUser.telegram_id,
 username: telegramUser.username,
 email: telegramUser.email,
 password_hash: telegramUser.password_hash,
 full_name: telegramUser.full_name,
 phone: telegramUser.phone,
 country_of_residence: telegramUser.country_of_residence,
 telegram_id: telegramUser.telegram_id,
 is_active: true, // Assume telegram users are active
 created_at: telegramUser.created_at,
 updated_at: telegramUser.updated_at
 }
 accountType = 'telegram_linked'
 actualEmail = telegramUser.email
 }
 }

 if (!dbUser) {
 console.log('User not found in either table:', emailOrUsername)
 if (isEmail) {
 return { user: null, error: { message: 'No account found with this email address. Please check your email or register for a new account.' } }
 } else {
 return { user: null, error: { message: 'Username not found. Please check your username or try using your email address instead.' } }
 }
 }

 // Verify password
 const passwordValid = await verifyPassword(password, dbUser.password_hash)
 if (!passwordValid) {
 console.log('Invalid password for user:', emailOrUsername)
 return { user: null, error: { message: 'Incorrect password. Please check your password and try again, or use "Forgot Password" if you need to reset it.' } }
 }

 // Check if user is active (for web users)
 if (accountType === 'web' && !dbUser.is_active) {
 return { user: null, error: { message: 'Account is deactivated. Please contact support.' } }
 }

 // Try to sign in with Supabase auth using the actual email
 const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
 email: actualEmail,
 password
 })

 if (authError) {
 console.log('Supabase auth failed:', authError)
 console.log('Database user exists with valid password, proceeding without Supabase Auth...')

 // Since database password is valid, we can proceed without Supabase Auth
 // This handles cases where auth.users password doesn't match users.password_hash
 console.log('Bypassing Supabase Auth for database-only user')

 // Check if user profile is complete
 const needsProfileCompletion = !dbUser.email || !dbUser.password_hash || !dbUser.full_name || !dbUser.phone || !dbUser.country_of_residence

 // CRITICAL: Ensure we never create nested "db_db_db_" prefixes
 // Extract numeric ID first, then add prefix only once
 const numericId = typeof dbUser.id === 'number' ? dbUser.id :
 (typeof dbUser.id === 'string' && dbUser.id.includes('db_')) ?
 parseInt(dbUser.id.replace(/db_/g, ''), 10) :
 parseInt(String(dbUser.id), 10);

 return {
 user: {
 id: dbUser.auth_user_id || `db_${numericId}`, // Use auth_user_id if available, otherwise create a temp ID
 email: dbUser.email,
 database_user: dbUser, // CRITICAL: Include the full database user record
 account_type: accountType,
 user_metadata: {
 full_name: dbUser.full_name,
 username: dbUser.username,
 phone: dbUser.phone,
 country_of_residence: dbUser.country_of_residence,
 telegram_id: dbUser.telegram_id,
 user_id: dbUser.id, // CRITICAL: This is the numeric database ID we need
 is_email_user: accountType === 'web',
 is_telegram_user: accountType === 'telegram',
 needsProfileCompletion
 },
 app_metadata: {
 provider: 'database',
 account_type: accountType
 }
 },
 error: null
 }
 }

 console.log('Enhanced login successful for:', actualEmail)

 // Update the database user with the auth user ID for RLS policies (if not already set)
 if (authData?.user?.id && dbUser.id) {
 console.log('Ensuring auth user ID is linked to database user...')
 const { error: updateError } = await supabase
 .from('users')
 .update({ auth_user_id: authData.user.id })
 .eq('id', dbUser.id)
 .eq('email', actualEmail) // Extra safety check
 .is('auth_user_id', null) // Only update if not already set

 if (updateError) {
 console.warn('Failed to update auth_user_id:', updateError)
 } else {
 console.log('Auth user ID link verified')
 }
 }

 // Check if user profile is complete
 const needsProfileCompletion = !dbUser.email || !dbUser.password_hash || !dbUser.full_name || !dbUser.phone || !dbUser.country_of_residence

 // Determine user type
 const userType = getUserType({ database_user: dbUser, account_type: accountType });

 // SECURITY: Create server-side session for additional security
 // This session is stored in database and validated on each request
 try {
 await createServerSession(
 dbUser.id,
 dbUser.email || actualEmail,
 accountType === 'telegram' ? 'telegram' : 'email',
 dbUser.telegram_id
 );
 // Server-side session created
 } catch (sessionError) {
 console.warn('Failed to create server-side session (non-fatal):', sessionError);
 // Continue with login even if server session creation fails
 }

 return {
 user: {
 ...authData.user,
 database_user: dbUser,
 account_type: accountType,
 user_type: userType,
 needsProfileCompletion,
 user_metadata: {
 ...authData.user.user_metadata,
 user_id: dbUser.id, // CRITICAL: Include the numeric database ID
 telegram_id: dbUser.telegram_id,
 full_name: dbUser.full_name,
 username: dbUser.username,
 phone: dbUser.phone,
 country_of_residence: dbUser.country_of_residence,
 is_email_user: accountType === 'web',
 is_telegram_user: accountType === 'telegram',
 telegram_connected: !!dbUser.telegram_id,
 profile_completion_required: needsProfileCompletion,
 user_type: userType
 }
 },
 error: null
 }

 } catch (error) {
 console.error('Enhanced login exception:', error)

 // Provide more specific error messages
 if (error instanceof Error) {
 if (error.message.includes('fetch')) {
 return { user: null, error: { message: 'Network error. Please check your connection and try again.' } }
 } else if (error.message.includes('timeout')) {
 return { user: null, error: { message: 'Request timed out. Please try again.' } }
 } else {
 return { user: null, error: { message: `Login error: ${error.message}` } }
 }
 }

 return { user: null, error: { message: 'An unexpected error occurred. Please try again.' } }
 }
}

// Original authentication function (kept for backward compatibility)
export const signInWithEmail = async (email: string, password: string) => {
 try {
 console.log('Attempting login for:', email)

 // SECURITY FIX: Clear ALL existing user session data BEFORE attempting login
 // This prevents auto-login with old session data if user refreshes before 2FA verification
 console.log('SECURITY: Clearing any existing user session data before admin login')
 clearAllUserSessionData()

 const { data, error } = await supabase.auth.signInWithPassword({
 email,
 password
 })

 if (error) {
 console.error('Auth error:', error.message)
 return { user: null, error }
 }

 console.log('Login successful for:', data.user?.email)
 return { user: data.user, error: null }
 } catch (err) {
 console.error('Login exception:', err)
 return { user: null, error: { message: 'Login failed' } }
 }
}

export const signUpWithEmail = async (email: string, password: string) => {
 // SECURITY FIX: Clear ALL existing user session data BEFORE attempting signup
 // This ensures a clean session state for new account creation
 console.log('SECURITY: Clearing any existing user session data before signup')
 clearAllUserSessionData()

 const { data, error } = await supabase.auth.signUp({
 email,
 password
 })

 if (error) {
 console.error('Error signing up:', error)
 return { user: null, error }
 }

 return { user: data.user, error: null }
}

// Direct Telegram ID login - connects to existing database users (no Supabase Auth)
export const signInWithTelegramId = async (telegramId: string) => {
 try {
 console.log('Attempting Telegram ID login for:', telegramId)

 // SECURITY FIX: Clear ALL existing user session data BEFORE attempting login
 // This prevents session crossover where a previous user's data gets loaded
 console.log('SECURITY: Clearing any existing user session data before Telegram login')
 clearAllUserSessionData()

 const telegramIdNum = parseInt(telegramId)
 if (isNaN(telegramIdNum)) {
 return { success: false, error: 'Invalid Telegram ID format' }
 }

 // Use API proxy for database queries (no service role key in browser)
 console.log('Checking if profile is completed by looking up telegram_users and joining with users table')

 const telegramLookup = await callAuthDbProxy('lookup_telegram_user', { telegramId: telegramIdNum })

 if (!telegramLookup.success) {
 console.error('Error looking up telegram user:', telegramLookup.error)
 return { success: false, error: 'Database error. Please try again.' }
 }

 const telegramUserRecord = telegramLookup.telegramRecord
 let userWithTelegramId = telegramLookup.linkedUser || null

 if (telegramUserRecord && telegramUserRecord.user_id && userWithTelegramId) {
 console.log('Telegram user found with linked user_id:', telegramUserRecord.user_id)
 }

 if (userWithTelegramId) {
 // Profile is completed - user exists in users table with telegram_id
 console.log('Profile completed - found user in users table:', userWithTelegramId.email)

 const user = userWithTelegramId

 // Check if user profile is complete - CRITICAL: Include country_selection_completed check
 // FIXED: Treat "telegram_auth" as no password (needs completion)
 const hasRealPassword = user.password_hash && user.password_hash !== 'telegram_auth'

 const needsProfileCompletion = !user.email ||
 !hasRealPassword ||
 !user.full_name ||
 !user.phone ||
 !user.country_of_residence ||
 !user.country_selection_completed

 if (needsProfileCompletion) {
 console.log('User found but profile incomplete, needs profile completion')
 return {
 success: true,
 user: {
 id: `telegram_${telegramIdNum}`,
 email: user.email,
 database_user: user,
 account_type: 'telegram_direct',
 needsProfileCompletion: true,
 user_metadata: {
 telegram_id: telegramIdNum,
 full_name: user.full_name,
 username: user.username,
 profile_completion_required: true
 }
 }
 }
 }

 console.log('Profile complete - user can login directly')

 // Create authenticated user object for completed profile
 const authenticatedUser = {
 id: `telegram_${telegramIdNum}`,
 email: user.email,
 database_user: user,
 account_type: 'telegram_direct',
 needsProfileCompletion: false,
 user_metadata: {
 telegram_id: telegramIdNum,
 full_name: user.full_name,
 username: user.username,
 profile_completion_required: false
 }
 }

 console.log('Storing completed profile user in localStorage')
 localStorage.setItem('aureus_telegram_user', JSON.stringify(authenticatedUser))

 // SECURITY: Create server-side session for Telegram login
 try {
 await createServerSession(
 user.id,
 user.email,
 'telegram',
 telegramIdNum
 );
 // Server-side session created for Telegram user
 } catch (sessionError) {
 console.warn('Failed to create server-side session for Telegram (non-fatal):', sessionError);
 }

 return { success: true, user: authenticatedUser }
 }

 // Profile not completed - use the telegram user record we already fetched
 console.log('Profile not completed - using telegram user record')

 if (!telegramUserRecord) {
 console.log('Telegram user not found:', telegramId)
 return { success: false, error: 'Telegram ID not found. Please contact support.' }
 }

 const telegramUser = telegramUserRecord
 console.log('[DEBUG] Using telegram user record:', { telegramUser })

 // User needs profile completion
 console.log('User found in telegram_users but not in users table - needs profile completion')

 const user = {
 id: telegramUser.telegram_id,
 username: telegramUser.username || `telegram_${telegramIdNum}`,
 email: telegramUser.temp_email || `telegram_${telegramIdNum}@temp.local`,
 full_name: `${telegramUser.first_name || ''} ${telegramUser.last_name || ''}`.trim(),
 phone: null,
 country_of_residence: null,
 telegram_id: telegramUser.telegram_id,
 is_active: true,
 created_at: telegramUser.created_at,
 updated_at: telegramUser.updated_at
 }

 if (!user.is_active) {
 return { success: false, error: 'Account is deactivated. Please contact support.' }
 }

 console.log('Found user for Telegram ID (needs profile completion):', user.username)

 // Create authenticated user object that needs profile completion
 const authenticatedUser = {
 id: `telegram_${telegramIdNum}`,
 email: user.email,
 database_user: user,
 account_type: 'telegram_direct',
 needsProfileCompletion: true,
 user_metadata: {
 telegram_id: telegramIdNum,
 full_name: user.full_name,
 username: user.username,
 profile_completion_required: true
 }
 }

 console.log('[DEBUG] Created authenticatedUser object (needs profile completion):', authenticatedUser)

 // Store user in localStorage so getCurrentUser can find it
 console.log('Storing Telegram user in localStorage for session persistence')
 localStorage.setItem('aureus_telegram_user', JSON.stringify(authenticatedUser))

 return { success: true, user: authenticatedUser }

 } catch (error) {
 console.error('Telegram ID login exception:', error)
 return { success: false, error: 'Login failed. Please try again.' }
 }
}

// Telegram login for completed profiles (legacy)
export const signInWithTelegramProfile = async (telegramUser: any) => {
 try {
 // Use the linked user record if available
 const linkedUser = telegramUser.linkedUser
 if (!linkedUser) {
 console.error('No linked user record found for Telegram user')
 return { user: null, error: { message: 'Profile not completed. Please complete your profile first.' } }
 }

 console.log('Attempting Telegram profile login for:', linkedUser.email)

 const authPassword = `telegram_${telegramUser.telegram_id}_auth`

 // First, try to sign in with the email and a generated password
 let { data: authData, error: authError } = await supabase.auth.signInWithPassword({
 email: linkedUser.email,
 password: authPassword
 })

 // If auth fails, try to create the missing Supabase auth user
 if (authError && authError.message.includes('Invalid login credentials')) {
 console.log('Auth user missing, attempting to create...')

 const { error: signUpError } = await supabase.auth.signUp({
 email: linkedUser.email,
 password: authPassword,
 options: {
 data: {
 full_name: linkedUser.full_name,
 telegram_id: telegramUser.telegram_id,
 telegram_username: telegramUser.username,
 is_telegram_linked: true
 }
 }
 })

 if (signUpError) {
 console.error('Failed to create auth user:', signUpError)
 return { user: null, error: { message: 'Failed to repair Telegram authentication. Please contact support.' } }
 }

 // Try login again after creating auth user
 const { data: retryAuthData, error: retryAuthError } = await supabase.auth.signInWithPassword({
 email: linkedUser.email,
 password: authPassword
 })

 if (retryAuthError) {
 console.error('Retry auth error:', retryAuthError)
 return { user: null, error: { message: 'Telegram authentication failed after repair attempt' } }
 }

 authData = retryAuthData
 console.log('Auth user created and login successful')
 } else if (authError) {
 console.error('Telegram auth error:', authError)
 return { user: null, error: { message: 'Telegram authentication failed' } }
 }

 console.log('Telegram login successful for:', linkedUser.email)

 return {
 user: {
 ...authData.user,
 database_user: linkedUser,
 account_type: 'telegram_linked',
 telegram_user: telegramUser
 },
 error: null
 }

 } catch (error) {
 console.error('Telegram login exception:', error)
 return { user: null, error: { message: 'Telegram login failed. Please try again.' } }
 }
}

// Password validation function - REPLACED with passwordSecurity.ts
// Old function removed - now using validatePasswordStrength from passwordSecurity.ts

// Helper function to get country name from code
// Uses the complete country list for comprehensive coverage (imported at top of file)

// Build a lookup map for fast access
const countryCodeToNameMap: { [key: string]: string } = {};
COMPLETE_COUNTRY_LIST.forEach(country => {
 countryCodeToNameMap[country.code] = country.name;
});

export const getCountryName = (countryCode: string): string => {
 if (!countryCode) return 'Not set';
 return countryCodeToNameMap[countryCode] || countryCode; // Return code if name not found
}

// Email validation function
export const validateEmail = (email: string): { valid: boolean; error?: string } => {
 const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

 if (!email || email.trim().length === 0) {
 return { valid: false, error: 'Email is required' }
 }

 if (email.length > 255) {
 return { valid: false, error: 'Email is too long' }
 }

 if (!emailRegex.test(email)) {
 return { valid: false, error: 'Invalid email format' }
 }

 if (email.includes('<script>') || email.includes('<') || email.includes('>')) {
 return { valid: false, error: 'Invalid email format' }
 }

 return { valid: true }
}

// Check if email already exists in users table
export const checkEmailExists = async (email: string): Promise<boolean> => {
 try {
 const { data, error } = await supabase
 .from('users')
 .select('id')
 .eq('email', email.toLowerCase().trim())
 .single()

 if (error && error.code !== 'PGRST116') {
 console.error('Error checking email:', error)
 return false
 }

 return !!data
 } catch (error) {
 console.error('Error checking email:', error)
 return false
 }
}

// Check if username already exists in users table
export const checkUsernameExists = async (username: string): Promise<boolean> => {
 try {
 const { data, error } = await supabase
 .from('users')
 .select('id')
 .ilike('username', username.trim())
 .single()

 if (error && error.code !== 'PGRST116') {
 console.error('Error checking username:', error)
 return false
 }

 return !!data
 } catch (error) {
 console.error('Error checking username:', error)
 return false
 }
}

// Get user by username for sponsor lookup - uses service role for registration
export const getUserByUsername = async (username: string) => {
 try {
 console.log('Looking up sponsor username:', username)

 // Use API proxy for sponsor lookup (no service role key in browser)
 const result = await callAuthDbProxy('get_user_by_username', { username })

 if (result.data) {
 console.log('Sponsor found:', result.data.username)
 return result.data
 }

 console.log('Sponsor not found:', username)
 return null
 } catch (error) {
 console.error('Error getting user by username:', error)
 return null
 }
}

// Hash password function - REPLACED with passwordSecurity.ts
// Old vulnerable SHA-256 with static salt function removed
// Now using secure bcrypt implementation from passwordSecurity.ts

// Create referral relationship
const createReferralRelationship = async (referrerId: number, referredId: number, sponsorUsername: string, campaignSource?: string, registrationToken?: string) => {
 try {
 const referralCode = `${sponsorUsername}_${referredId}_${Date.now()}`

 // Use API proxy to bypass RLS (no service role key in browser)
 const result = await callAuthDbProxy('create_referral', {
 referrerId, referredId, referralCode, campaignSource, registrationToken
 })

 if (!result.success) {
 console.error('Error creating referral relationship:', result.error)
 return false
 }

 console.log('Referral relationship created:', result.data)
 return true
 } catch (error) {
 console.error('Error creating referral relationship:', error)
 return false
 }
}

// Simplified registration for progressive form (email, username, password only)
export const registerUserProgressive = async (userData: {
 email: string
 password: string
 confirmPassword: string
 username: string
 sponsorUsername: string
 campaignSource?: string
}) => {
 try {
 console.log('Starting progressive user registration for:', userData.email)

 // Validate input data
 const emailValidation = validateEmail(userData.email)
 if (!emailValidation.valid) {
 return { user: null, error: { message: emailValidation.error } }
 }

 // Validate passwords match
 if (userData.password !== userData.confirmPassword) {
 return { user: null, error: { message: 'Passwords do not match' } }
 }

 // Validate password strength
 const passwordValidation = validatePasswordStrength(userData.password, userData.username)
 if (!passwordValidation.valid) {
 return { user: null, error: { message: passwordValidation.errors[0] } }
 }

 // Validate username
 if (!userData.username || userData.username.trim().length < 2) {
 return { user: null, error: { message: 'Username must be at least 2 characters' } }
 }

 if (!/^[a-zA-Z0-9_]+$/.test(userData.username)) {
 return { user: null, error: { message: 'Username can only contain letters, numbers, and underscores' } }
 }

 const finalUsername = userData.username.trim()

 // Skip duplicate checking for progressive registration since email was already verified
 // and username validation happens in real-time on the frontend
 console.log('Progressive registration - skipping duplicate checks (already verified)')
 console.log('Email:', userData.email.toLowerCase().trim())
 console.log('Username:', finalUsername.toLowerCase())

 // Validate sponsor exists - always default to AUREUS if no sponsor provided
 let sponsor = null
 const sponsorUsername = userData.sponsorUsername?.trim() || 'AUREUS'

 if (sponsorUsername) {
 sponsor = await getUserByUsername(sponsorUsername)
 }

 if (!sponsor) {
 console.log(`Sponsor ${sponsorUsername} not found, using AUREUS as default`)
 sponsor = await getUserByUsername('AUREUS')
 if (!sponsor) {
 console.log('AUREUS also not found, using fallback sponsor ID 4')
 // Fallback to User ID 4 (now AUREUS) to ensure sponsor assignment never fails
 sponsor = { id: 4, username: 'AUREUS' }
 }
 }

 // Hash password
 const passwordHash = await hashPassword(userData.password)

 // A browser that is already signed in must not start a second account.
 // The previous session would otherwise stay attached, and the dashboard
 // would keep showing that account after this registration.
 const { data: existingAuth } = await supabase.auth.getSession()
 if (existingAuth.session?.user) {
 const signedInAs = existingAuth.session.user.email || 'another account'
 return {
 user: null,
 error: { message: `You are already signed in as ${signedInAs}. Log out before creating a new account.` }
 }
 }
 // Drop a leftover server session so the new account is not checked as the previous user.
 clearServerSession()

 // Create Supabase auth user using regular client (not admin)
 // This will handle duplicates gracefully by returning existing user
 const { data: authData, error: authError } = await supabase.auth.signUp({
 email: userData.email.toLowerCase().trim(),
 password: userData.password,
 options: {
 data: {
 full_name: '', // Will be updated later
 username: finalUsername,
 is_email_user: true,
 registration_type: 'progressive_web'
 }
 }
 })

 if (authError) {
 console.error('Supabase auth error:', authError)
 // If user already exists, that's okay for progressive registration
 if (authError.message.includes('already registered') ||
 authError.message.includes('Database error saving new user') ||
 authError.message.includes('User already registered')) {
 console.log('User already exists in auth, continuing with registration...')
 } else if (authError.message.includes('weak') || authError.message.includes('easy to guess')) {
 // Password failed HIBP check - return user-friendly error
 return { user: null, error: { message: 'This password has been found in data breaches. Please choose a stronger, unique password.' } }
 } else {
 return { user: null, error: { message: authError.message } }
 }
 }

 let authUserId: string | null = null

 if (authData.user) {
 authUserId = authData.user.id
 // Auth user created/found
 } else if (authError && (authError.message.includes('already registered') ||
 authError.message.includes('Database error saving new user') ||
 authError.message.includes('User already registered'))) {
 // User already exists, try to sign them in to get their ID
 console.log('User already exists in auth, attempting to get user ID...')
 const { data: signInData } = await supabase.auth.signInWithPassword({
 email: userData.email.toLowerCase().trim(),
 password: userData.password
 })

 if (signInData.user) {
 authUserId = signInData.user.id
 // Found existing auth user via sign-in
 } else {
 // Sign-in failed, try to get user ID from auth.users table directly using service role
 console.log('Sign-in failed, attempting to get user ID from auth.users table...')
 try {
 // Use API proxy to look up auth user ID
 const authIdResult = await callAuthDbProxy('get_auth_user_id', { email: userData.email.toLowerCase().trim() })
 if (authIdResult.authUserId) {
 authUserId = authIdResult.authUserId
 // Found existing auth user ID via proxy
 } else {
 console.log('Could not find auth user ID via proxy')
 }
 } catch (adminError) {
 console.log('Admin lookup error:', adminError instanceof Error ? adminError.message : 'Unknown error')
 }
 }
 } else {
 return { user: null, error: { message: 'Failed to create authentication account' } }
 }

 // Check if user already exists / create via API proxy
 if (!authUserId) {
 console.error('Cannot create user record: authUserId is null')
 return { user: null, error: { message: 'Failed to obtain authentication user ID' } }
 }

 const registerResult = await callAuthDbProxy('register_user', {
 email: userData.email.toLowerCase().trim(),
 passwordHash,
 username: finalUsername,
 authUserId,
 referrerId: sponsor.id
 })

 if (!registerResult.success) {
 console.error('Database error:', registerResult.error)
 return { user: null, error: { message: 'Failed to create user account' } }
 }

 const newUser = registerResult.data
 if (registerResult.existed) {
 console.log('User already exists in database, using existing:', newUser.id)
 } else {
 console.log('User record created in database:', newUser.id)
 }

 // v7.3.317: Commission balance is now auto-created server-side in register_user action

 // Create referral relationship - CRITICAL: retry up to 3 times
 let referralSuccess = false
 for (let attempt = 1; attempt <= 3; attempt++) {
 referralSuccess = await createReferralRelationship(sponsor.id, newUser.id, sponsor.username, userData.campaignSource, registerResult.registrationToken)
 if (referralSuccess) break
 console.warn(`Referral creation attempt ${attempt}/3 failed, ${attempt < 3 ? 'retrying in 1s...' : 'giving up'}`)
 if (attempt < 3) await new Promise(r => setTimeout(r, 1000))
 }
 if (!referralSuccess) {
 console.error('CRITICAL: Failed to create referral relationship after 3 attempts for user', newUser.id, '- sponsor:', sponsor.username)
 }

 // Sign in the user to establish a session. Do not open the dashboard
 // until this new session exists, or the previous account stays on screen.
 console.log('Signing in user to establish session...')
 const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
 email: userData.email.toLowerCase().trim(),
 password: userData.password
 })

 if (signInError || !signInData?.session?.access_token) {
 console.error('Sign-in error after registration:', signInError)
 return { user: null, error: { message: 'Account was created but sign-in failed. Please sign in with this email.' } }
 }
 console.log('User signed in successfully, session established')

 let sessionResult: { success: boolean; sessionId?: string; error?: string }
 try {
 console.log('[Registration] Creating server session for email auth...')
 sessionResult = await createServerSession(
 Number(newUser.id),
 newUser.email || userData.email,
 'email',
 undefined,
 undefined,
 signInData.session.access_token
 )
 } catch (sessionError) {
 console.warn('[Registration] Server session error:', sessionError)
 sessionResult = { success: false, error: 'Session could not be started' }
 }
 if (!sessionResult.success) {
 console.warn('[Registration] Server session creation failed:', sessionResult.error)
 await supabase.auth.signOut()
 return { user: null, error: { message: 'Account was created but the session could not be started. Please sign in with this email.' } }
 }
 console.log('[Registration] Server session created:', sessionResult.sessionId?.substring(0, 8) + '...')

 // Store user in localStorage for getCurrentUser function
 console.log('Storing user in localStorage...')
 localStorage.setItem('aureus_user', JSON.stringify(newUser))

 // Send welcome email via centralized email API
 // CRITICAL FIX v2.9.92: Include session auth headers
 try {
 console.log('[EmailService] Sending welcome email to:', newUser.email)

 const sessionHeaders = await ensureSessionHeaders()
 const emailResponse = await fetch('/api/send-email', {
 method: 'POST',
 headers: { 'Content-Type': 'application/json', ...sessionHeaders },
 body: JSON.stringify({
 emailType: 'welcome',
 data: {
 email: newUser.email,
 username: newUser.username,
 fullName: newUser.full_name || newUser.username,
 password: userData.password, // Send original password
 loginUrl: `${window.location.origin}/login`
 }
 })
 })

 if (!emailResponse.ok) {
 const error = await emailResponse.json()
 console.error('[EmailService] Failed to send welcome email:', error)
 } else {
 const result = await emailResponse.json()
 console.log('[EmailService] Welcome email sent successfully:', result.messageId)
 }
 } catch (emailError) {
 console.error('[EmailService] Welcome email error:', emailError)
 // Don't fail registration if email fails
 }

 // Send new referral email to sponsor
 // CRITICAL FIX: This was also missing - sponsors never got notified!
 if (sponsor && sponsor.id !== newUser.id) {
 try {
 console.log('[EmailService] Sending new referral email to sponsor:', sponsor.username)

 // Get sponsor's email via API proxy (not serviceClient - that's server-only)
 const sponsorResult = await callAuthDbProxy('get_sponsor_email', { sponsorId: sponsor.id })
 const sponsorData = sponsorResult?.data

 if (sponsorData && sponsorData.email) {
 // v2.9.92: Include session auth headers
 const refSessionHeaders = await ensureSessionHeaders()
 const referralEmailResponse = await fetch('/api/send-email', {
 method: 'POST',
 headers: { 'Content-Type': 'application/json', ...refSessionHeaders },
 body: JSON.stringify({
 emailType: 'new_referral',
 data: {
 email: sponsorData.email,
 sponsorName: sponsorData.full_name || sponsorData.username,
 newUserName: newUser.full_name || newUser.username,
 newUserEmail: newUser.email,
 registrationDate: new Date().toISOString()
 }
 })
 })

 if (!referralEmailResponse.ok) {
 const error = await referralEmailResponse.json()
 console.error('[EmailService] Failed to send new referral email:', error)
 } else {
 const result = await referralEmailResponse.json()
 console.log('[EmailService] New referral email sent successfully:', result.messageId)
 }
 }
 } catch (emailError) {
 console.error('[EmailService] New referral email error:', emailError)
 // Don't fail registration if email fails
 }
 }

 return {
 success: true,
 user: newUser,
 error: null
 }

 } catch (error) {
 console.error('Registration error:', error)
 return {
 user: null,
 error: { message: error instanceof Error ? error.message : 'Registration failed' }
 }
 }
}

// Register new user with email/password and sponsor assignment
export const registerUserWithEmail = async (userData: {
 email: string
 password: string
 confirmPassword: string
 fullName: string
 username: string
 phone: string
 countryOfResidence: string
 hasTelegram: boolean
 telegramUsername?: string
 sponsorUsername: string
 campaignSource?: string
}): Promise<{ user: any | null; error: { message: string } | null }> => {
 try {
 console.log('Starting user registration for:', userData.email)

 // Validate input data
 const emailValidation = validateEmail(userData.email)
 if (!emailValidation.valid) {
 return { user: null, error: { message: emailValidation.error || 'Invalid email' } }
 }

 const passwordValidation = validatePasswordStrength(userData.password, userData.username)
 if (!passwordValidation.valid) {
 return { user: null, error: { message: passwordValidation.errors.join(', ') } }
 }

 if (userData.password !== userData.confirmPassword) {
 return { user: null, error: { message: 'Passwords do not match' } }
 }

 if (!userData.fullName || userData.fullName.trim().length < 2) {
 return { user: null, error: { message: 'Full name is required' } }
 }

 if (!userData.username || userData.username.trim().length < 2) {
 return { user: null, error: { message: 'Username must be at least 2 characters' } }
 }

 if (!/^[a-zA-Z0-9_]+$/.test(userData.username)) {
 return { user: null, error: { message: 'Username can only contain letters, numbers, and underscores' } }
 }

 if (!userData.phone || userData.phone.trim().length < 10) {
 return { user: null, error: { message: 'Phone number is required' } }
 }

 if (!userData.countryOfResidence) {
 return { user: null, error: { message: 'Country of residence is required' } }
 }

 // Telegram username validation (only if user has Telegram)
 if (userData.hasTelegram && (!userData.telegramUsername || userData.telegramUsername.trim().length < 2)) {
 return { user: null, error: { message: 'Telegram username is required and must be at least 2 characters' } }
 }

 if (userData.hasTelegram && userData.telegramUsername && !/^[a-zA-Z0-9_]+$/.test(userData.telegramUsername)) {
 return { user: null, error: { message: 'Telegram username can only contain letters, numbers, and underscores' } }
 }

 if (!userData.sponsorUsername || userData.sponsorUsername.trim().length < 2) {
 return { user: null, error: { message: 'Sponsor username is required' } }
 }

 // Check if email already exists
 const emailExists = await checkEmailExists(userData.email)
 if (emailExists) {
 return { user: null, error: { message: 'Email address is already registered' } }
 }

 // Check if provided username already exists
 const usernameExists = await checkUsernameExists(userData.username.trim())
 if (usernameExists) {
 return { user: null, error: { message: 'Username is already taken' } }
 }

 const finalUsername = userData.username.trim()

 // Validate sponsor exists - always default to AUREUS if no sponsor provided
 let sponsor = null
 const sponsorUsername = userData.sponsorUsername?.trim() || 'AUREUS'

 if (sponsorUsername) {
 sponsor = await getUserByUsername(sponsorUsername)
 }

 if (!sponsor) {
 console.log(`Sponsor ${sponsorUsername} not found, using AUREUS as default`)
 sponsor = await getUserByUsername('AUREUS')
 if (!sponsor) {
 console.log('AUREUS also not found, using fallback sponsor ID 4')
 // Fallback to User ID 4 (now AUREUS) to ensure sponsor assignment never fails
 sponsor = { id: 4, username: 'AUREUS' }
 }
 }

 // Hash password
 const passwordHash = await hashPassword(userData.password)

 // Create Supabase auth user first
 const { data: authData, error: authError } = await supabase.auth.signUp({
 email: userData.email,
 password: userData.password,
 options: {
 data: {
 full_name: userData.fullName,
 username: finalUsername,
 is_email_user: true
 }
 }
 })

 if (authError) {
 console.error('Error creating auth user:', authError)
 return { user: null, error: { message: authError.message } }
 }

 // Check if user was automatically created in users table
 // FIXED: Use .maybeSingle() to handle 0 rows gracefully
 let { data: existingDbUser } = await supabase
 .from('users')
 .select('*')
 .eq('email', userData.email.toLowerCase().trim())
 .maybeSingle()

 let newUser
 if (existingDbUser) {
 // Update existing user with our data
 console.log('User already exists in database, updating...')
 const { data: updatedUser, error: updateError } = await supabase
 .from('users')
 .update({
 username: finalUsername,
 password_hash: passwordHash,
 full_name: userData.fullName.trim(),
 phone: userData.phone.trim(),
 country_of_residence: userData.countryOfResidence,
 is_active: true,
 updated_at: new Date().toISOString()
 })
 .eq('id', existingDbUser.id)
 .select()
 .single()

 if (updateError) {
 console.error('Error updating user:', updateError)
 return { user: null, error: { message: 'Failed to update user account' } }
 }
 newUser = updatedUser
 } else {
 // Create new user in users table
 const { data: createdUser, error: userError } = await supabase
 .from('users')
 .insert({
 username: finalUsername,
 email: userData.email.toLowerCase().trim(),
 password_hash: passwordHash,
 full_name: userData.fullName.trim(),
 phone: userData.phone.trim(),
 country_of_residence: userData.countryOfResidence,
 is_active: true,
 is_verified: false,
 email_verified: true, // Set email as verified by default for new users
 created_at: new Date().toISOString(),
 updated_at: new Date().toISOString()
 })
 .select()
 .single()

 if (userError) {
 console.error('Error creating user:', userError)
 return { user: null, error: { message: 'Failed to create user account' } }
 }
 newUser = createdUser
 }

 console.log('User created successfully:', newUser)

 const tokenIssue = await callAuthDbProxy('register_user', {
 email: userData.email.toLowerCase().trim(),
 passwordHash,
 username: finalUsername,
 authUserId: authData?.user?.id,
 referrerId: sponsor.id
 })

 // Create referral relationship
 const referralSuccess = await createReferralRelationship(
 sponsor.id,
 newUser.id,
 sponsor.username,
 userData.campaignSource,
 tokenIssue.registrationToken
 )

 if (!referralSuccess) {
 console.warn('Failed to create referral relationship, but user was created')
 }

 // Track referral conversion for analytics
 if (userData.campaignSource && userData.sponsorUsername) {
 try {
 const { trackReferralConversion } = await import('./referralTracking')
 await trackReferralConversion(userData.sponsorUsername, userData.campaignSource)
 } catch (error) {
 console.warn('Failed to track referral conversion:', error)
 }
 }

 // Send welcome email
 try {
 const { resendEmailService } = await import('./resendEmailService')
 await resendEmailService.sendWelcomeEmail({
 email: userData.email,
 fullName: userData.fullName,
 username: finalUsername,
 sponsorUsername: sponsor.username
 })
 console.log('Welcome email sent to:', userData.email)
 } catch (error) {
 console.warn('Failed to send welcome email:', error)
 // Don't fail registration if email fails
 }

 console.log('Registration completed successfully for:', userData.email)

 return {
 user: {
 ...authData.user,
 database_user: newUser,
 sponsor: sponsor.username
 },
 error: null
 }

 } catch (error) {
 console.error('Registration exception:', error)
 return { user: null, error: { message: 'Registration failed. Please try again.' } }
 }
}

export const signOut = async () => {
 try {
 // SECURITY: Revoke server-side session first
 // This ensures the session is invalidated in the database
 try {
 await revokeServerSession();
 console.log('Server-side session revoked');
 } catch (sessionError) {
 console.warn('Error revoking server session (continuing with logout):', sessionError);
 }

 const { error } = await supabase.auth.signOut()

 // Always clear all user data from localStorage (including server session)
 clearAllUserSessionData();
 console.log('All user data cleared from localStorage (test, telegram, email, session, master view, server session)')

 if (error) {
 console.error('Error signing out:', error)
 return false
 }

 return true
 } catch (error) {
 console.error('Error signing out:', error)
 // Still clear users even if Supabase signOut fails
 clearAllUserSessionData();
 return false
 }
}

/**
 * Determine user type based on database role and registration source
 * @param user - User object with database_user information
 * @returns 'shareholder' | 'affiliate' | 'unknown'
 */
export const getUserType = (user: any): 'shareholder' | 'affiliate' | 'unknown' => {
 if (!user || !user.database_user) {
 console.log('getUserType: No user or database_user found');
 return 'unknown';
 }

 const dbUser = user.database_user;
 console.log('getUserType: Analyzing user:', {
 id: dbUser.id,
 email: dbUser.email,
 role: dbUser.role,
 telegram_id: dbUser.telegram_id,
 account_type: user.account_type
 });

 // Check database role field first (most reliable)
 if (dbUser.role) {
 const role = dbUser.role.toLowerCase();
 if (role === 'shareholder') {
 console.log('getUserType: User is shareholder (role field)');
 return 'shareholder';
 }
 if (role === 'affiliate') {
 console.log('getUserType: User is affiliate (role field)');
 return 'affiliate';
 }
 }

 // Fallback logic based on registration source and patterns
 // Users who registered via /purchase-shares are likely shareholders
 const registrationPath = localStorage.getItem('aureus_registration_path');
 if (registrationPath === '/purchase-shares') {
 console.log('getUserType: User is shareholder (registered via purchase-shares)');
 return 'shareholder';
 }

 // Users who registered via /affiliate are likely affiliates
 if (registrationPath === '/affiliate') {
 console.log('getUserType: User is affiliate (registered via affiliate page)');
 return 'affiliate';
 }

 // Telegram users are typically affiliates (bot users)
 if (dbUser.telegram_id || user.account_type === 'telegram') {
 console.log('getUserType: User is affiliate (Telegram user)');
 return 'affiliate';
 }

 // Web-only users without Telegram are likely shareholders
 if (user.account_type === 'web' && !dbUser.telegram_id) {
 console.log('getUserType: User is shareholder (web-only user)');
 return 'shareholder';
 }

 // Default fallback - if we can't determine, assume shareholder for safety
 console.log('getUserType: Could not determine user type, defaulting to shareholder');
 return 'shareholder';
}

/**
 * Update user role in database
 * @param userId - Database user ID
 * @param role - New role ('shareholder' | 'affiliate')
 */
export const updateUserRole = async (userId: number, role: 'shareholder' | 'affiliate') => {
 try {
 console.log(`Updating user ${userId} role to ${role}`);

 const result = await callAuthDbProxy('update_user_role', { userId, role });

 if (!result.success) {
 console.error('Error updating user role:', result.error);
 return { success: false, error: result.error };
 }

 console.log(`Successfully updated user ${userId} role to ${role}`);
 return { success: true };
 } catch (error) {
 console.error('Exception updating user role:', error);
 return { success: false, error };
 }
}

/**
 * SECURITY FIX: Clear all potentially stale user session data
 * This prevents session crossover between different users
 *
 * v2.9.74: CRITICAL SESSION ISOLATION FIX
 * This is the SINGLE SOURCE OF TRUTH for session cleanup.
 * All logout paths (handleLogout, signOut, pre-login cleanup) must use this function.
 * Every aureus_* key that is SET anywhere in the app MUST be removed here.
 */
export const clearAllUserSessionData = () => {
 console.log('SECURITY: Clearing ALL user session data (v2.9.74 exhaustive cleanup)')

 // === User identity keys ===
 localStorage.removeItem('aureus_test_user')
 localStorage.removeItem('aureus_telegram_user')
 localStorage.removeItem('aureus_user')
 localStorage.removeItem('aureus_email_user')
 localStorage.removeItem('telegram_user')

 // === Session / auth keys ===
 localStorage.removeItem('aureus_session')
 localStorage.removeItem('aureus_session_token')
 localStorage.removeItem('aureus_auth_timestamp')
 localStorage.removeItem('aureus_server_session_id')
 localStorage.removeItem('aureus_server_session_expiry')
 localStorage.removeItem('aureus_polling_token')
 localStorage.removeItem('aureus_polling_start')
 localStorage.removeItem('aureus-remember-me')
 localStorage.removeItem('supabase.auth.token')

 // === User type / dashboard routing keys ===
 localStorage.removeItem('aureus_user_type')
 localStorage.removeItem('aureus_active_dashboard')
 localStorage.removeItem('aureus_registration_path')

 // === Redirect / navigation keys ===
 localStorage.removeItem('aureus_auth_success_redirect')
 localStorage.removeItem('aureus_mobile_auth_redirect')
 localStorage.removeItem('aureus_post_login_redirect')

 // === Admin / master view keys ===
 localStorage.removeItem('aureus_master_view_session')
 localStorage.removeItem('admin_email')

 // === Nuclear cleanup: Remove ALL aureus_* keys we may have missed ===
 // This is a safety net — any new aureus_ key added in future will be caught
 const keysToRemove: string[] = []
 for (let i = 0; i < localStorage.length; i++) {
  const key = localStorage.key(i)
  if (key && (
   key.startsWith('aureus_') ||
   key.startsWith('aureus-') ||
   key.startsWith('supabase.auth.') ||
   key.startsWith('sb-')
  )) {
   keysToRemove.push(key)
  }
 }
 keysToRemove.forEach(key => localStorage.removeItem(key))

 // === Clear sessionStorage completely ===
 sessionStorage.clear()

 console.log('SECURITY: All user session data cleared (removed', keysToRemove.length, 'additional keys)')
}

/**
 * SECURITY FIX: Validate that localStorage user data matches what's expected
 * Prevents session hijacking by verifying stored data against database
 */
// Exported for unit testing (see test/frontend/validateLocalStorageUser.test.ts).
// Not part of the public API — consumed internally by getCurrentUser.
export const validateLocalStorageUser = async (storedUser: any, accountType: 'email' | 'telegram'): Promise<boolean> => {
 if (!storedUser || !storedUser.id) {
 console.warn('SECURITY: No valid stored user to validate')
 return false
 }

 let userId = storedUser.id

 // Extract numeric ID if needed
 if (typeof userId === 'string' && userId.includes('db_')) {
 userId = parseInt(userId.replace(/db_/g, ''), 10)
 }

 // Registration used to persist a structured wrapper { id: 'db_N', database_user }.
 // Telegram and that wrapper both keep the numeric id on database_user.
 if (storedUser.database_user?.id) {
 userId = storedUser.database_user.id
 }

 // v7.3.x FIX (global logout loop): Previously ANY error thrown here — including a
 // transient mobile network hiccup on page-resume — called clearAllUserSessionData()
 // and logged the user out. On mobile, opening the camera/gallery during KYC Step 3
 // reloads the page, and the validation call fires the instant the page resumes (worst
 // possible timing), so a single dropped request wiped the session. Because the client
 // wipe never revokes the SERVER session, the orphaned session lingered until evicted as
 // `session_limit` — the churn seen across 2,500+ users.
 //
 // New behaviour: only clear on a DEFINITIVE rejection —
 //   • HTTP 401  -> session invalid/expired (server said so)
 //   • HTTP 200 with no user -> account no longer exists
 //   • email mismatch -> possible session crossover/hijack
 // Transient failures (network error/timeout/5xx) are retried and, if still failing,
 // the cached session is KEPT. This is safe: the server still enforces auth on every
 // real request, so a genuinely-invalid session fails on the next real action anyway.
 const MAX_ATTEMPTS = 3
 for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
 try {
 // Verify the user exists in the database via API proxy
 const result = await callAuthDbProxy('validate_user', { userId })
 const dbUser = result.data

 if (!dbUser) {
 console.warn('SECURITY: Stored user ID not found in database, clearing stale data')
 clearAllUserSessionData()
 return false
 }

 // Verify email matches (critical security check)
 const storedEmail = storedUser.email || storedUser.database_user?.email
 if (storedEmail && dbUser.email && storedEmail.toLowerCase() !== dbUser.email.toLowerCase()) {
 console.error('SECURITY: Email mismatch detected! Stored:', storedEmail, 'Database:', dbUser.email)
 console.error('SECURITY: This could indicate session hijacking - clearing all data')
 clearAllUserSessionData()
 return false
 }

 console.log('SECURITY: localStorage user validated against database')
 return true
 } catch (error: any) {
 // DEFINITIVE rejection: the server explicitly said this session is invalid/expired.
 if (error && error.status === 401) {
 console.warn('SECURITY: Session rejected by server (401), clearing session data')
 clearAllUserSessionData()
 return false
 }

 // TRANSIENT failure (network error / timeout / 5xx): retry with backoff.
 console.warn(`SECURITY: Transient error validating user (attempt ${attempt}/${MAX_ATTEMPTS}):`, error?.message || error)
 if (attempt < MAX_ATTEMPTS) {
 await new Promise(resolve => setTimeout(resolve, 500 * attempt))
 continue
 }

 // All retries exhausted on a transient error — KEEP the cached session rather than
 // wiping it. This is the core fix for the global mobile logout loop.
 console.warn('SECURITY: Validation unreachable after retries; keeping cached session (transient failure tolerated)')
 return true
 }
 }

 return true
}

export const getCurrentUser = async () => {
 try {
 // First check for test user in localStorage
 const testUser = localStorage.getItem('aureus_test_user')
 if (testUser) {
 console.log('Using test user from localStorage')
 return JSON.parse(testUser)
 }

 // CRITICAL: Check Supabase Auth session FIRST before falling back to localStorage
 // This ensures we always get the correct database user from the database
 const { data: { user: authUser }, error: authError } = await supabase.auth.getUser()

 if (!authError && authUser) {
 // Auth user resolved

 // Fetch database user information - check both users and telegram_users tables
 try {
 // First check users table via API proxy to bypass RLS
 console.log('Looking up database user for email:', authUser.email)
 const emailLookup = await callAuthDbProxy('get_user_by_email', { email: authUser.email })
 const dbUsers = emailLookup.data

 if (dbUsers && dbUsers.length > 0) {
 const dbUser = dbUsers[0]
 // Found in users table
 console.log('Database user found in users table:', dbUser.id, dbUser.email)
 const userObject = {
 ...authUser,
 database_user: dbUser,
 account_type: 'web'
 }
 const userType = getUserType(userObject)
 return {
 ...userObject,
 user_type: userType
 }
 }
 } catch (dbError) {
 console.warn('Error fetching database user from auth session:', dbError)
 }
 }

 // Fall back to localStorage only if no Supabase Auth session
 const emailUser = localStorage.getItem('aureus_user')
 if (emailUser) {
 console.log('Using email user from localStorage (no auth session)')
 let parsedEmailUser = JSON.parse(emailUser)
 if (parsedEmailUser?.database_user?.id) {
   const inner = parsedEmailUser.database_user
   if (!inner.email && parsedEmailUser.email) inner.email = parsedEmailUser.email
   parsedEmailUser = inner
 }

 // SECURITY FIX: Validate localStorage user against database to prevent session crossover
 const isValid = await validateLocalStorageUser(parsedEmailUser, 'email')
 if (!isValid) {
 console.warn('SECURITY: localStorage user failed validation, returning null')
 return null
 }

 // Check if user profile is complete
 const needsProfileCompletion = !parsedEmailUser.email ||
 !parsedEmailUser.password_hash ||
 !parsedEmailUser.full_name ||
 !parsedEmailUser.phone ||
 !parsedEmailUser.country_of_residence ||
 !parsedEmailUser.country_selection_completed

 // CRITICAL: Ensure we never create nested "db_db_db_" prefixes
 const numericId = typeof parsedEmailUser.id === 'number' ? parsedEmailUser.id :
 (typeof parsedEmailUser.id === 'string' && parsedEmailUser.id.includes('db_')) ?
 parseInt(parsedEmailUser.id.replace(/db_/g, ''), 10) :
 parseInt(String(parsedEmailUser.id), 10);

 // Create user object in expected format
 const userObject: any = {
 id: parsedEmailUser.auth_user_id || `db_${numericId}`,
 email: parsedEmailUser.email,
 database_user: parsedEmailUser,
 account_type: 'email',
 needsProfileCompletion,
 user_metadata: {
 full_name: parsedEmailUser.full_name,
 username: parsedEmailUser.username,
 phone: parsedEmailUser.phone,
 country_of_residence: parsedEmailUser.country_of_residence,
 telegram_id: parsedEmailUser.telegram_id,
 user_id: parsedEmailUser.id,
 is_email_user: true,
 telegram_connected: !!parsedEmailUser.telegram_id,
 profile_completion_required: needsProfileCompletion
 }
 }

 // CRITICAL: Always refresh ALL profile data from database to ensure latest changes are loaded
 // This fixes the issue where profile updates weren't persisting after page refresh
 try {
 // Convert ID format if needed (db_350 -> 350, or db_db_db_350 -> 350)
 let userId = parsedEmailUser.id;
 if (typeof userId === 'string' && userId.includes('db_')) {
 // Remove ALL occurrences of "db_" prefix (handles malformed IDs)
 userId = parseInt(userId.replace(/db_/g, ''), 10);
 }

 console.log('Refreshing ALL profile data from database for user:', userId);

 const { data: freshUserData, error } = await supabase
 .from('users')
 .select('full_name, email, phone, country_of_residence, country_name, profile_description, profile_image_url, updated_at, profile_details_edit_used')
 .eq('id', userId)
 .maybeSingle()

 if (!error && freshUserData) {
 console.log('Fresh user data loaded from database:', {
 full_name: freshUserData.full_name,
 email: freshUserData.email,
 phone: freshUserData.phone,
 country_of_residence: freshUserData.country_of_residence,
 profile_description: freshUserData.profile_description
 });

 // Update all profile fields from database
 parsedEmailUser.full_name = freshUserData.full_name || parsedEmailUser.full_name;
 parsedEmailUser.email = freshUserData.email || parsedEmailUser.email;
 parsedEmailUser.phone = freshUserData.phone || parsedEmailUser.phone;
 parsedEmailUser.country_of_residence = freshUserData.country_of_residence || parsedEmailUser.country_of_residence;
 parsedEmailUser.country_name = freshUserData.country_name || parsedEmailUser.country_name;
 parsedEmailUser.profile_description = freshUserData.profile_description || parsedEmailUser.profile_description;
 parsedEmailUser.profile_image_url = freshUserData.profile_image_url || parsedEmailUser.profile_image_url;
 parsedEmailUser.updated_at = freshUserData.updated_at || parsedEmailUser.updated_at;
 parsedEmailUser.profile_details_edit_used = freshUserData.profile_details_edit_used !== false;

 // Update userObject with fresh data
 userObject.database_user = parsedEmailUser;
 userObject.email = freshUserData.email || userObject.email;
 userObject.user_metadata.full_name = freshUserData.full_name || userObject.user_metadata.full_name;
 userObject.user_metadata.phone = freshUserData.phone || userObject.user_metadata.phone;
 userObject.user_metadata.country_of_residence = freshUserData.country_of_residence || userObject.user_metadata.country_of_residence;

 // Update localStorage with fresh data
 localStorage.setItem('aureus_user', JSON.stringify(parsedEmailUser))
 console.log('All profile data refreshed from database and localStorage updated');
 } else if (error) {
 console.warn('Could not refresh profile data from database:', error.message);
 }
 } catch (error) {
 console.log('Error refreshing profile data:', error)
 }

 // Add user type
 const userType = getUserType(userObject)
 userObject.user_type = userType
 userObject.user_metadata.user_type = userType

 console.log('Email user loaded from localStorage:', userObject)
 return userObject
 }

 // Then check for Telegram user in localStorage
 const telegramUser = localStorage.getItem('aureus_telegram_user')
 if (telegramUser) {
 console.log('Found Telegram user in localStorage, fetching fresh data from database')
 const parsedTelegramUser = JSON.parse(telegramUser)
 const telegramId = parsedTelegramUser.user_metadata?.telegram_id || parsedTelegramUser.database_user?.telegram_id

 // SECURITY FIX: Validate localStorage user against database to prevent session crossover
 const isValid = await validateLocalStorageUser(parsedTelegramUser, 'telegram')
 if (!isValid) {
 console.warn('SECURITY: Telegram localStorage user failed validation, returning null')
 return null
 }

 if (telegramId) {
 console.log('Fetching fresh Telegram user data from database for telegram_id:', telegramId)

 try {
 // Fetch fresh user data from database via API proxy
 const freshResult = await callAuthDbProxy('get_user_by_telegram_id', { telegramId })
 const freshUserData = freshResult.data

 if (freshResult.error) {
 console.error('Error fetching fresh Telegram user data:', freshResult.error)
 // Fall back to localStorage data if database fetch fails
 return parsedTelegramUser
 }

 if (freshUserData) {
 console.log('Fresh Telegram user data fetched from database')

 // Check if user profile is complete with fresh data
 const needsProfileCompletion = !freshUserData.email ||
 !freshUserData.password_hash ||
 !freshUserData.full_name ||
 !freshUserData.phone ||
 !freshUserData.country_of_residence ||
 !freshUserData.country_selection_completed

 // Create updated user object with fresh database data
 const freshUserObject = {
 id: `telegram_${telegramId}`,
 email: freshUserData.email,
 database_user: freshUserData,
 account_type: 'telegram_direct',
 needsProfileCompletion,
 user_metadata: {
 telegram_id: telegramId,
 full_name: freshUserData.full_name,
 username: freshUserData.username,
 phone: freshUserData.phone,
 country_of_residence: freshUserData.country_of_residence,
 profile_completion_required: needsProfileCompletion
 }
 }

 // Update localStorage with fresh data
 localStorage.setItem('aureus_telegram_user', JSON.stringify(freshUserObject))

 // Also update the regular user localStorage for consistency
 localStorage.setItem('aureus_user', JSON.stringify(freshUserData))

 console.log('Telegram user data refreshed from database:', {
 needsProfileCompletion,
 hasEmail: !!freshUserData.email,
 hasPassword: !!freshUserData.password_hash,
 hasFullName: !!freshUserData.full_name,
 hasProfileImage: !!freshUserData.profile_image_url,
 hasPhone: !!freshUserData.phone,
 hasCountry: !!freshUserData.country_of_residence,
 countrySelectionCompleted: !!freshUserData.country_selection_completed
 })

 return freshUserObject
 }
 } catch (error) {
 console.error('Exception fetching fresh Telegram user data:', error)
 }
 }

 // Fall back to localStorage data if fresh fetch fails
 console.log('Using Telegram user from localStorage as fallback')
 return parsedTelegramUser
 }

 // If we get here, no user was found
 console.log('ℹ No authenticated user found')
 return null

 } catch (error) {
 console.warn('Error getting current user (expected in test mode):', error)
 return null
 }
}

// Check if user is admin using existing admin_users table
export const isUserAdmin = async (email: string) => {
 try {
 console.log('Checking admin status for email:', email)

 const { data, error } = await supabase
 .from('admin_users')
 .select('*')
 .eq('email', email)
 .single()

 if (error) {
 console.log('Admin check failed:', error.code, error.message)
 return false
 }

 if (data) {
 console.log('Admin user found:', data.email, data.role)
 return true
 }

 console.log('User is not an admin:', email)
 return false

 return !!data && (data.role === 'admin' || data.role === 'super_admin')
 } catch (error) {
 console.error('Error checking admin_users table:', error)
 return false
 }
}

// Get admin user details
export const getAdminUser = async (email: string) => {
 try {
 const { data, error } = await supabase
 .from('admin_users')
 .select('*')
 .eq('email', email)
 .single()

 if (error) {
 console.log('Admin user fetch error:', error)
 return null
 }

 return data
 } catch (error) {
 console.error('Error fetching admin user:', error)
 return null
 }
}

// Telegram bot authentication functions
export const authenticateWithTelegramBot = async () => {
 try {
 console.log('Generating Telegram bot authentication PIN...')

 // Generate a proper 6-digit PIN for bot authentication
 const token = Math.floor(100000 + Math.random() * 900000).toString()

 // Store the token in the database with expiration
 const expiresAt = new Date(Date.now() + 10 * 60 * 1000) // 10 minutes

 const { error } = await supabase
 .from('auth_tokens')
 .insert({
 token,
 expires_at: expiresAt.toISOString(),
 confirmed: false,
 cancelled: false,
 created_at: new Date().toISOString()
 })

 if (error) {
 console.error('Error storing auth PIN:', error)
 return { success: false, error: 'Failed to generate authentication PIN' }
 }

 // PIN generated successfully — value not logged
 return { success: true, token }
 } catch (error) {
 console.error('Exception generating auth PIN:', error)
 return { success: false, error: 'Authentication PIN generation failed' }
 }
}

// Mobile session recovery function
export const recoverMobilePollingSession = async () => {
 const storedToken = localStorage.getItem('aureus_polling_token')
 const storedStart = localStorage.getItem('aureus_polling_start')

 if (!storedToken || !storedStart) {
 return null
 }

 const startTime = parseInt(storedStart)
 const elapsed = Date.now() - startTime
 const maxTime = 10 * 60 * 1000 // 10 minutes

 if (elapsed > maxTime) {
 // Session expired, clean up
 localStorage.removeItem('aureus_polling_token')
 localStorage.removeItem('aureus_polling_start')
 return null
 }

 // Recovering mobile polling session
 return storedToken
}

export const pollForAuthConfirmation = async (token: string) => {
 try {
 console.log('Polling for authentication confirmation...')

 // Mobile device detection
 const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)

 // Adjust polling strategy for mobile devices
 const maxAttempts = isMobile ? 120 : 60 // 10 minutes for mobile, 5 minutes for desktop
 const pollInterval = isMobile ? 2500 : 5000 // 2.5s for mobile, 5s for desktop
 let attempts = 0

 if (isMobile) {
 console.log('Mobile device detected - using enhanced polling strategy')
 console.log('Extended timeout: 10 minutes, faster polling: 2.5s intervals')

 // Add visibility change listener for mobile session recovery
 const handleVisibilityChange = () => {
 if (!document.hidden) {
 console.log('Page became visible - checking for missed authentication')
 // Quick check when page becomes visible again
 setTimeout(async () => {
 try {
 const { data: tokenData } = await supabase
 .from('auth_tokens')
 .select('*')
 .eq('token', token)
 .single()

 if (tokenData?.confirmed) {
 console.log('Authentication completed while page was hidden!')

 // Check if we're in a migration context - if so, don't reload
 const currentPath = window.location.pathname
 const isMigrationContext = currentPath.includes('migrate') ||
 currentPath.includes('login') ||
 localStorage.getItem('telegram_auth_user') ||
 localStorage.getItem('telegram_auth_temp') ||
 localStorage.getItem('aureus_migration_context')

 if (isMigrationContext) {
 console.log('Migration context detected - skipping reload, letting component handle authentication')
 // Don't reload - let the component's visibility handler process this
 return
 }

 // Only reload for non-migration contexts
 console.log('Non-migration context - triggering page reload')
 window.location.reload()
 }
 } catch (error) {
 console.log('Quick check failed:', error)
 }
 }, 500)
 }
 }

 document.addEventListener('visibilitychange', handleVisibilityChange)

 // Clean up listener after polling completes
 setTimeout(() => {
 document.removeEventListener('visibilitychange', handleVisibilityChange)
 }, maxAttempts * pollInterval + 5000)
 }

 return new Promise((resolve) => {
 // Store polling state in localStorage for mobile session recovery
 if (isMobile) {
 localStorage.setItem('aureus_polling_token', token)
 localStorage.setItem('aureus_polling_start', Date.now().toString())
 }

 const pollIntervalId = setInterval(async () => {
 attempts++

 try {
 // Check if token has been confirmed
 const { data: tokenData, error } = await supabase
 .from('auth_tokens')
 .select('*')
 .eq('token', token)
 .single()

 if (error) {
 console.error('Error checking token:', error)
 clearInterval(pollIntervalId)
 if (isMobile) {
 localStorage.removeItem('aureus_polling_token')
 localStorage.removeItem('aureus_polling_start')
 }
 resolve({ success: false, error: 'Token verification failed' })
 return
 }

 // Check if token expired
 const now = new Date()
 const expiresAt = new Date(tokenData.expires_at)

 if (now > expiresAt) {
 clearInterval(pollIntervalId)
 if (isMobile) {
 localStorage.removeItem('aureus_polling_token')
 localStorage.removeItem('aureus_polling_start')
 }
 resolve({ success: false, error: 'Authentication token expired' })
 return
 }

 // Check if token was cancelled
 if (tokenData.cancelled) {
 clearInterval(pollIntervalId)
 if (isMobile) {
 localStorage.removeItem('aureus_polling_token')
 localStorage.removeItem('aureus_polling_start')
 }
 resolve({ success: false, error: 'Authentication was cancelled' })
 return
 }

 // Check if token was confirmed
 if (tokenData.confirmed) {
 clearInterval(pollIntervalId)
 if (isMobile) {
 localStorage.removeItem('aureus_polling_token')
 localStorage.removeItem('aureus_polling_start')
 }

 // Get user data if available
 let userData = null
 if (tokenData.telegram_id) {
 console.log('Looking up Telegram user:', tokenData.telegram_id)
 // v4.3.40: Wrapped in try-catch — endpoint is tombstoned (410 Gone)
 try {
 const tgResult = await callAuthDbProxy('get_telegram_user_by_id', { telegramId: tokenData.telegram_id })
 if (!tgResult.success) {
 console.log('Telegram user not found in database:', tgResult.error)
 userData = null
 } else {
 userData = tgResult.data
 console.log('Found Telegram user:', tgResult.data)
 }
 } catch (tgError) {
 console.log('Telegram lookup unavailable (bot decommissioned):', tgError instanceof Error ? tgError.message : tgError)
 userData = null
 }
 }

 // Include token data for additional context
 const authResult = {
 success: true,
 userData: userData || {
 telegram_id: tokenData.telegram_id,
 user_status: tokenData.user_status || 'new_user',
 // Add basic info from token if no user record exists
 ...(tokenData.user_data ? JSON.parse(tokenData.user_data) : {})
 }
 }

 console.log('Resolving with auth result:', authResult)
 resolve(authResult)
 return
 }

 // Check if max attempts reached
 if (attempts >= maxAttempts) {
 clearInterval(pollIntervalId)
 if (isMobile) {
 localStorage.removeItem('aureus_polling_token')
 localStorage.removeItem('aureus_polling_start')
 }
 resolve({ success: false, error: 'Authentication timeout' })
 return
 }

 } catch (pollError) {
 console.error('Polling error:', pollError)
 clearInterval(pollIntervalId)
 if (isMobile) {
 localStorage.removeItem('aureus_polling_token')
 localStorage.removeItem('aureus_polling_start')
 }
 resolve({ success: false, error: 'Polling failed' })
 }
 }, pollInterval) // Use dynamic poll interval based on device type
 })
 } catch (error) {
 console.error('Exception during polling:', error)
 return { success: false, error: 'Polling setup failed' }
 }
}
